import { PodhiveLogo } from "@/components/taxpeerlogo.component";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { applyActionCode, getAuth } from "firebase/auth";
import { captureAuthException } from "@/lib/auth/captureAuthException";

type VerificationState = "verifying" | "success" | "error";

/**
 * Email Verification Page
 *
 * Handles the Firebase email verification flow using the oobCode from the email link.
 * Based on Firebase's custom email handler documentation:
 * https://firebase.google.com/docs/auth/custom-email-handler
 *
 * Flow:
 * 1. Extract oobCode from URL parameters
 * 2. Verify the code with Firebase using applyActionCode
 * 3. Show success or error message
 * 4. Redirect to dashboard or login
 */
const EmailVerificationPage = () => {
	const { t } = useTranslation("common");
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	const [state, setState] = useState<VerificationState>("verifying");
	const [error, setError] = useState<string | null>(null);

	/**
	 * Verify the email using the action code
	 */
	const verifyEmail = useCallback(
		async (code: string) => {
			try {
				const auth = getAuth();
				// Apply the action code to verify the email
				await applyActionCode(auth, code);
				setState("success");

				// Redirect to dashboard after 3 seconds
				setTimeout(() => {
					navigate("/dashboard");
				}, 3000);
			} catch (error) {
				console.error("Failed to verify email:", error);
				captureAuthException(error, "email_verification");
				setState("error");

				// Provide specific error messages based on Firebase error codes
				const firebaseError = error as { code?: string };
				if (firebaseError.code === "auth/expired-action-code") {
					setError(t("emailVerification.expiredLink"));
				} else if (firebaseError.code === "auth/invalid-action-code") {
					setError(t("emailVerification.invalidLink"));
				} else if (firebaseError.code === "auth/user-disabled") {
					setError(t("emailVerification.userDisabled"));
				} else if (firebaseError.code === "auth/user-not-found") {
					setError(t("emailVerification.userNotFound"));
				} else {
					setError(t("emailVerification.verificationFailed"));
				}
			}
		},
		[navigate, t],
	);

	// Extract and verify the oobCode on mount
	useEffect(() => {
		const code = searchParams.get("oobCode");
		const mode = searchParams.get("mode");

		// Validate that we have the correct parameters
		if (!code || mode !== "verifyEmail") {
			setState("error");
			setError(t("emailVerification.invalidLink"));
			return;
		}

		verifyEmail(code);
	}, [searchParams, t, verifyEmail]);

	/**
	 * Render loading state while verifying
	 */
	if (state === "verifying") {
		return (
			<div className="flex min-h-screen flex-col items-center justify-center bg-card p-4">
				<div className="w-full max-w-[400px] space-y-8 text-center">
					<div className="flex justify-center">
						<PodhiveLogo width={125} bildmarke={false} />
					</div>
					<div className="space-y-4">
						<Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
						<h1 className="text-xl font-semibold text-foreground">
							{t("emailVerification.verifying")}
						</h1>
						<p className="text-sm text-muted-foreground">
							{t("emailVerification.verifyingDescription")}
						</p>
					</div>
				</div>
			</div>
		);
	}

	/**
	 * Render error state if verification failed
	 */
	if (state === "error") {
		return (
			<div className="flex min-h-screen flex-col items-center justify-center bg-card p-4">
				<div className="w-full max-w-[400px] space-y-8">
					<div className="flex justify-center">
						<PodhiveLogo width={125} bildmarke={false} />
					</div>

					<div className="rounded-lg bg-destructive/10 border-2 border-destructive/50 p-6 text-center space-y-4">
						<XCircle className="h-12 w-12 mx-auto text-destructive" />
						<h1 className="text-xl font-semibold text-destructive">
							{t("emailVerification.errorTitle")}
						</h1>
						<p className="text-sm text-destructive/90">{error}</p>
					</div>

					<div className="space-y-4">
						<Button
							onClick={() => navigate("/auth/login")}
							className="w-full"
							size="lg"
						>
							{t("emailVerification.backToLogin")}
						</Button>

						<div className="text-center text-sm text-muted-foreground">
							{t("emailVerification.needNewLink")}
						</div>
					</div>

					<div className="flex justify-center space-x-4 text-sm text-muted-foreground">
						<Link to="/agb">{t("footer.terms")}</Link>
						<Link to="/datenschutz">{t("footer.privacy")}</Link>
					</div>
				</div>
			</div>
		);
	}

	/**
	 * Render success state after email verification
	 */
	return (
		<div className="flex min-h-screen flex-col items-center justify-center bg-card p-4">
			<div className="w-full max-w-[400px] space-y-8">
				<div className="flex justify-center">
					<PodhiveLogo width={125} bildmarke={false} />
				</div>

				<div className="rounded-lg bg-green-500/10 border-2 border-green-500/50 p-6 text-center space-y-4">
					<CheckCircle2 className="h-12 w-12 mx-auto text-green-600 dark:text-green-400" />
					<h1 className="text-xl font-semibold text-green-700 dark:text-green-300">
						{t("emailVerification.successTitle")}
					</h1>
					<p className="text-sm text-green-600 dark:text-green-400">
						{t("emailVerification.successMessage")}
					</p>
					<div className="pt-2">
						<Loader2 className="h-5 w-5 animate-spin mx-auto text-green-600 dark:text-green-400" />
						<p className="text-xs text-green-600/70 dark:text-green-400/70 mt-2">
							{t("emailVerification.redirecting")}
						</p>
					</div>
				</div>

				<Button
					onClick={() => navigate("/dashboard")}
					className="w-full"
					size="lg"
				>
					{t("emailVerification.goToDashboard")}
				</Button>

				<div className="flex justify-center space-x-4 text-sm text-muted-foreground">
					<Link to="/agb">{t("footer.terms")}</Link>
					<Link to="/datenschutz">{t("footer.privacy")}</Link>
				</div>
			</div>
		</div>
	);
};

export default EmailVerificationPage;
